/*
 * Назначение: общий layout приватной зоны админки.
 * Функционал: sidebar + header + main, автозаголовок раздела по URL,
 * поддержка свернутого/развёрнутого сайдбара. На телефоне (< md) меню
 * спрятано и выезжает поверх страницы по кнопке в шапке.
 */

import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";

import { MQ_DESKTOP_NAV, useMediaQuery } from "@/lib/use-media-query";
import { Header } from "./header";
import { Sidebar } from "./sidebar";

/** Сопоставление префикса пути и заголовка раздела. */
const TITLES: Array<{ prefix: string; title: string }> = [
  { prefix: "/dashboard", title: "Дашборд" },
  { prefix: "/knowledge-base", title: "База знаний" },
  { prefix: "/dialogs", title: "Диалоги" },
  { prefix: "/leads", title: "Заявки" },
  { prefix: "/analytics", title: "Аналитика" },
  { prefix: "/settings", title: "Настройки" },
  { prefix: "/integrations", title: "Интеграции" },
  { prefix: "/security", title: "Безопасность" },
  { prefix: "/users", title: "Пользователи" },
  { prefix: "/profile", title: "Профиль" },
  { prefix: "/projects/new", title: "Создать проект" },
];

const STORAGE_KEY_COLLAPSED = "ai-seller-sidebar-collapsed";

export function AdminLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const isDesktopNav = useMediaQuery(MQ_DESKTOP_NAV);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    return localStorage.getItem(STORAGE_KEY_COLLAPSED) === "1";
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_COLLAPSED, collapsed ? "1" : "0");
  }, [collapsed]);

  // Переход в другой раздел или поворот экрана до ширины ПК — меню закрываем.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, isDesktopNav]);

  // Открытое меню на телефоне: Esc закрывает, страница под ним не прокручивается.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [mobileOpen]);

  const title = useMemo(() => {
    const found = TITLES.find((t) => location.pathname.startsWith(t.prefix));
    return found?.title ?? "ИИ-продавец";
  }, [location.pathname]);

  return (
    // h-dvh: на телефоне 100vh больше видимой области (адресная строка), низ обрезался.
    <div className="flex h-screen h-dvh overflow-hidden bg-background text-foreground">
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
          aria-hidden="true"
          onClick={() => setMobileOpen(false)}
        />
      )}
      <Sidebar
        collapsed={isDesktopNav && collapsed}
        onToggleCollapse={() => setCollapsed((c) => !c)}
        mobileOpen={mobileOpen}
        onNavigate={() => setMobileOpen(false)}
      />
      <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
        <Header
          title={title}
          systemStatus="healthy"
          onMenuClick={() => setMobileOpen(true)}
          menuOpen={mobileOpen}
        />
        <main className="flex-1 min-w-0 overflow-auto">{children}</main>
      </div>
    </div>
  );
}
